'use client';

import { ActivityWithRelations, WorkoutWithRelations } from '@/utils/types';

import type { Analysis } from '@trainme/db/client';
import { create } from 'zustand';
import { defaultWorkout } from '@trainme/api/client';

interface CalendarState {
  activity: ActivityWithRelations | null;
  setActivity: (activity: ActivityWithRelations | null) => void;
  activities: ActivityWithRelations[];
  setActivities: (activities: ActivityWithRelations[]) => void;
  workout: WorkoutWithRelations | null;
  setWorkout: (workout: WorkoutWithRelations | null) => void;
  workouts: WorkoutWithRelations[];
  setWorkouts: (workouts: WorkoutWithRelations[]) => void;
  analyses: Analysis[];
  setAnalyses: (analyses: Analysis[]) => void;
  scheduleDate: Date;
  setScheduleDate: (date: Date) => void;
  showChatWindow: boolean;
  setShowChatWindow: (show: boolean) => void;
  /**
   * Id of the CoachMessage that ChatWindow should scroll into view once its
   * messages are loaded ("coach say" surfaces deep-linking back to
   * their source chat message). Cleared once consumed; a missing/stale/
   * not-yet-loaded id is a no-op, never an error.
   */
  chatScrollToMessageId: string | null;
  setChatScrollToMessageId: (messageId: string | null) => void;
  /** Opens the chat window, optionally scrolled to a specific message. */
  openChatAtMessage: (messageId?: string | null) => void;
  syncedWeeks: Record<string, boolean>;
  setWeekSynced: (weekStart: string, value: boolean) => void;
}

export const useCalendarState = create<CalendarState>((set) => ({
  activity: null,
  setActivity: (activity) => set({ activity }),
  activities: [],
  setActivities: (activities) => set({ activities }),
  workout: { ...defaultWorkout, userId: '', activityId: null },
  setWorkout: (workout) => set({ workout }),
  workouts: [],
  setWorkouts: (workouts) => set({ workouts }),
  analyses: [],
  setAnalyses: (analyses) => set({ analyses }),
  scheduleDate: new Date(),
  setScheduleDate: (date) => set({ scheduleDate: date }),
  showChatWindow: false,
  setShowChatWindow: (show) => set({ showChatWindow: show }),
  chatScrollToMessageId: null,
  setChatScrollToMessageId: (messageId) =>
    set({ chatScrollToMessageId: messageId }),
  openChatAtMessage: (messageId) =>
    set({
      showChatWindow: true,
      chatScrollToMessageId: messageId ?? null,
    }),
  syncedWeeks: {},
  setWeekSynced: (weekStart, value) =>
    set((state) => ({
      syncedWeeks: { ...state.syncedWeeks, [weekStart]: value },
    })),
}));
